function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react';
import { create, InstanceProps } from 'react-modal-promise';
import clsx from 'clsx';
import { sprig } from '@sprig-technologies/sprig-browser';
import { ModalReact, ModalType } from 'common/components/modal';
import { withStyles } from 'common/util/react';
import { IconSizeType } from 'common/constants';
import { useTranslation } from 'common/i18n/hooks';
import { DEFAULT_USER_NAME } from 'common/report/util/profile/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { reporter } from 'common/report/error-reporting';
import { getProfilePath } from 'common/report/util/profile/utils';
import { UserLocalStorageHelper } from 'common/report/user-local-storage';
import { LocalStorageKey } from 'common/report/constants';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { IronContextRegistry } from 'common/report/context-registry';
import { Visibility } from 'common/components/base/stackable';
import { ButtonTheme } from 'common/components/button/constants';
import { TableReact } from 'common/components/table';
import { InputReact } from 'common/components/input';
import { InputSize } from 'common/components/input/types';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ButtonReact } from 'common/components/button';
import { useDomEvent } from 'common/hooks/useDomEvent';
import { getLocation } from 'common/util/url';
import { ReplayBanner } from 'common/widgets/replay-banner';
import { featureSamplingUtils, getSessionRecordingBlockingInfo } from 'common/report/util/feature-sampling';
import { getProjectId } from 'common/report/util';
import { CurrentProject } from 'common/report/model-resources/projects';
import { SwitchReact } from 'common/components/switch';
import { SvgIconReact } from 'common/components/svg-icon';
import { TooltipReact } from 'common/components/tooltip';
import { DropMenuReact } from 'common/components/drop-menu';
import { PropertiesMenuReact } from 'common/query-builder/query-block/properties-menu';
import { Directions } from 'common/components/drop-menu/types';
import { Property } from 'common/query-builder/query-block/properties-menu/types';
import { renameEvent } from 'common/report/util/events';
import { TrackedUserIconReact } from '../tracked-user-icon';
import { CopyToClipboardButtonReact } from '../copy-to-clipboard/copy-to-clipboard-button';
import { CopyType } from '../copy-to-clipboard/copy-to-clipboard-button/types';
import { promptFeedbackModal } from '../feedback-modal';
import { FeedbackForFeature, Sentiment } from '../feedback-modal/types';
import { DateRange } from '../date-range-picker/models/date-range';
import { getBannerTypeToDisplay, ReplayBannerType } from '../replay-banner/util';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import tableCss from './table.inline.styl';
import eventIndicatorCss from './event-indicator/index.module.styl?inline';
import { EmbedErrorStateType, RecordingEventMetadata, RecordingEvents, RecordingMetadata, RecordingPlayerSource, RecordingPlayerTrackingEvent, ReplayErrorStateType } from './types';
import { RecordingPlayer, REPORT_EVENT_TIME_JUMP_OFFSET } from './player';
import { DummyPlaceHolderReplayPlaylistRow, ReplayPlaylistRowEl } from './playlist-row';
import { ReplayPlaylistRowEventMetadataEl } from './playlist-row-events';
import { filterPinnedReplayProperties, formatRecordingProgressTimeBar, getDisplayDate, getDisplayName, getDisplayTime, getPinnedReplayPropertyDisabledStatus, getPinnedReplayPropertyNames, PINNED_REPLAY_PROPERTIES, useReplayEvents, useReplayPageViewEvents } from './util';
import { ReplayPlaylistRowContextMenuReact } from './playlist-row-context-menu';
import { REPLAY_EVENT } from './query-utils';
const PERSISTENCE_KEY = "replay-modal-persistence-key";
const SPRIG_SURVEY_EVENT = "SessionReplayModalClosed";
const Sprig = sprig.configure({
  environmentId: "9sZai6GO-WSS"
});
type RecordingPlaylistPlayerProps = {
  replays: RecordingMetadata[];
  replayDateRange?: DateRange;
  open: boolean;
  onClose: () => void;
  source: RecordingPlayerSource;
  loadingReplays?: boolean; // Used to indicate that the replays are still being queried outside the component
  enableUpdateURL?: boolean; // Used to enable URL updating feature
  startingTimestamp?: string; // Used to start the replay at a specific timestamp
  startingReplayId?: string; // Used to start the playlist at a replay
  initialEvents?: RecordingEvents[]; // Used to pass in events if they are already queried
  disableInitialEventsSearch?: boolean; // Used to avoid filling search when passing in initial events
  extraTrackingProps?: Record<string, any>;
} & Pick<IronContextRegistry, `localStorageManager`>;
type PersistedState = {
  seenReplayIds: string[];
};
enum SortOption {
  MostRecent = "MostRecent",
  LeastRecent = "LeastRecent",
  LongestDuration = "LongestDuration",
  ShortestDuration = "ShortestDuration",
  MostEvents = "MostEvents",
  LeastEvents = "LeastEvents",
}
const SortOptionLabels = {
  [SortOption.MostRecent]: _ref => {
    let {
      t
    } = _ref;
    return t("session-replay.sort.most-recent");
  },
  [SortOption.LeastRecent]: _ref2 => {
    let {
      t
    } = _ref2;
    return t("session-replay.sort.least-recent");
  },
  [SortOption.LongestDuration]: _ref3 => {
    let {
      t
    } = _ref3;
    return t("session-replay.sort.longest-duration");
  },
  [SortOption.ShortestDuration]: _ref4 => {
    let {
      t
    } = _ref4;
    return t("session-replay.sort.shortest-duration");
  },
  [SortOption.MostEvents]: _ref5 => {
    let {
      t
    } = _ref5;
    return t("session-replay.sort.most-events");
  },
  [SortOption.LeastEvents]: _ref6 => {
    let {
      t
    } = _ref6;
    return t("session-replay.sort.least-events");
  }
};
function isSkipInactivityExperimentEnabled(currentProject?: CurrentProject) {
  const projectId = (currentProject === null || currentProject === void 0 ? void 0 : currentProject.id.toString()) || getProjectId();
  return featureSamplingUtils.determineFeatureEligibility(projectId, "session-replay-skip-inactivity", 50);
}
export function RecordingPlaylistPlayer(props: RecordingPlaylistPlayerProps) {
  const startingReplayIdx = props.startingReplayId ? props.replays.findIndex(replay => replay.replayId === props.startingReplayId) : 0;
  const [currentRecordingIdx, setCurrentRecordingIdx] = useState<number>(startingReplayIdx >= 0 ? startingReplayIdx : 0);
  const [viewedRecordings, setViewedRecordings] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(SortOption.MostRecent);
  const [visibleRecordingsCount, setVisibleRecordingsCount] = useState<number>(props.replays.length);
  const [playlistCollapsed, setPlaylistCollapsed] = useState<boolean>(false);
  const [sortedReplays, setSortedReplays] = useState<RecordingMetadata[]>([]);
  const [currentRecordingProgressTime, setCurrentRecordingProgressTime] = useState<number>(0);
  const [copyRecordingProgressTime, setCopyRecordingProgressTime] = useState<number>(0);
  const [currentRecordingEventSelectedTime, setCurrentRecordingEventSelectedTime] = useState<number>(null);
  const [currentRecordingPaused, setCurrentRecordingPaused] = useState<boolean>(false);
  const [isStitchingEnabled, setIsStitchingEnabled] = useState<boolean>(true);
  const [expandedRowsReplayIds, setExpandedRowsReplayIds] = useState<Set<string>>(new Set());
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [replayBannerType, setReplayBannerType] = useState<ReplayBannerType>(ReplayBannerType.NoBanner);
  const [isSkipInactivityEnabled, setIsSkipInactivityEnabled] = useState<boolean>(false);
  const [currentContextMenuRecordingIdx, setCurrentContextMenuRecordingIdx] = useState<number | null>(null);
  const [replayLoadedProps, setReplayLoadedProps] = useState<Obj>(null);
  const [hoveredEvent, setHoveredEvent] = useState<RecordingEventMetadata | null>(null);
  const [customizePropertiesMenuOpen, setCustomizePropertiesMenuOpen] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState<Property[]>(PINNED_REPLAY_PROPERTIES);
  const playlistTableRef = useRef<HTMLDivElement>(null);
  const currentSelectedRowRef = useRef<HTMLDivElement>(null);
  const playlistModalRef = useRef<HTMLDivElement>(null);
  const currentContextMenuRowRef = useRef<HTMLDivElement>(null);
  const customizePropertiesButtonRef = useRef(null);
  const currentProject = useCurrentProject();
  const enableSkipInactivity = isSkipInactivityExperimentEnabled(currentProject.get().value);
  const blockedReplayFrames = getSessionRecordingBlockingInfo(currentProject.get().value);
  const currentRecording = sortedReplays[currentRecordingIdx];
  function getTrackingProps(replayToTrack?: RecordingMetadata) {
    replayToTrack = replayToTrack || currentRecording;
    const replayProps = replayToTrack ? {
      tracked_replay_duration: replayToTrack.duration,
      replay_sdk: replayToTrack.source,
      replay_id: replayToTrack.replayId,
      replay_distinct_id: replayToTrack.distinctId,
      replay_retention_period: replayToTrack.retentionPeriod,
      is_stitched: replayToTrack.isStitchedFromEvent
    } : {};
    return _objectSpread(_objectSpread(_objectSpread({}, props.extraTrackingProps || {}), {}, {
      banner_type: replayBannerType,
      blocked_replay_frames: blockedReplayFrames,
      is_fullscreen: isFullscreen,
      is_in_skip_inactivity_experiment: enableSkipInactivity,
      is_loading_replays: props.loadingReplays,
      is_playlist_collapsed: playlistCollapsed,
      is_server_side_stitching_enabled: true,
      is_skip_inactivity_enabled: isSkipInactivityEnabled,
      num_played: viewedRecordings.size,
      num_replays_loaded: props.replays.length,
      num_replays_stitched: props.replays.filter(r => r.isStitchedFromEvent).length,
      selected_sort_option: selectedSortOption,
      tracked_replay_duration: currentRecording === null || currentRecording === void 0 ? void 0 : currentRecording.duration,
      replay_sdk: currentRecording === null || currentRecording === void 0 ? void 0 : currentRecording.source,
      source: props.source
    }, replayProps), replayLoadedProps !== null && replayLoadedProps !== void 0 ? replayLoadedProps : {});
  }
  const userLocalStorageHelper: UserLocalStorageHelper<PersistedState> = new UserLocalStorageHelper({
    context: props.localStorageManager,
    namespace: LocalStorageKey.SeenReplayIds
  });
  const {
    t,
    print
  } = useTranslation();
  const {
    recordingsEventMetadataLoading,
    getReplayEventsForId,
    hasReplayPageViewEventsFromEventsQuery
  } = useReplayEvents(props.replays, sortedReplays[currentRecordingIdx], props.initialEvents || null, selectedProperties);
  const {
    replayPageViewEventsLoaded,
    getReplayPageViewEventsForId,
    hasReplayPageViewEvents
  } = useReplayPageViewEvents(props.replays, currentProject.get().value);
  useEffect(() => {
    setViewedRecordings(getPlayedReplays());
    setTimeout(() => scrollCurrentRecordingToTop(), 200);
    return () => {
      var _userCookieUtils$getU;
      Sprig("identifyAndTrack", {
        eventName: SPRIG_SURVEY_EVENT,
        userId: (_userCookieUtils$getU = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU === void 0 ? void 0 : _userCookieUtils$getU.id.toString(),
        showSurveyCallback: () => Promise.resolve(true)
      });
    };
  }, []);
  useDomEvent(document, "fullscreenchange", () => onFullscreenChange());
  useEffect(() => {
    var _props$replayDateRang;
    const replaysCopy = [...props.replays];
    const _replayBannerType = getBannerTypeToDisplay(blockedReplayFrames, (_props$replayDateRang = props.replayDateRange) === null || _props$replayDateRang === void 0 ? void 0 : _props$replayDateRang.getCalendarDatesAsOf());
    setReplayBannerType(_replayBannerType);
    const newSortedReplays = sortReplaysBySortOption(replaysCopy);
    setSortedReplays(newSortedReplays);
    if (newSortedReplays.length > 0) {
      if (!props.startingTimestamp && !props.disableInitialEventsSearch) {
        var _props$initialEvents, _targetEvent$replayEv;
        const targetEvent = (_props$initialEvents = props.initialEvents) === null || _props$initialEvents === void 0 ? void 0 : _props$initialEvents.find(entry => entry.replayId.includes(newSortedReplays[currentRecordingIdx].replayId));
        if (targetEvent && ((_targetEvent$replayEv = targetEvent.replayEvents[0]) === null || _targetEvent$replayEv === void 0 ? void 0 : _targetEvent$replayEv.eventName) !== REPLAY_EVENT) {
          var _targetEvent$replayEv2, _targetEvent$replayEv3;
          const targetTime = ((_targetEvent$replayEv2 = targetEvent.replayEvents[0]) === null || _targetEvent$replayEv2 === void 0 ? void 0 : _targetEvent$replayEv2.eventTime) || 0;
          const targetEventName = ((_targetEvent$replayEv3 = targetEvent.replayEvents[0]) === null || _targetEvent$replayEv3 === void 0 ? void 0 : _targetEvent$replayEv3.eventName) || "";
          setCurrentRecordingEventSelectedTime(Math.max(targetTime - REPORT_EVENT_TIME_JUMP_OFFSET, 0));
          setSearchFilter(renameEvent(targetEventName));
        }
      } else {
        setCurrentRecordingEventSelectedTime(getTimestampFromReplaySeconds(props.startingTimestamp, newSortedReplays));
      }
    }
  }, [props.replays]);
  function getTimestampFromReplaySeconds(secondsInString: string, newSortedReplays: RecordingMetadata[]) {
    let secondsIn = 0;
    try {
      secondsIn = parseFloat(secondsInString);
    } catch (_unused) {
      secondsIn = 0;
    }
    if (newSortedReplays.length > 0 && secondsIn) {
      return newSortedReplays[currentRecordingIdx].time + secondsIn;
    }
    return null;
  }
  function hasBeenPlayed(replayId: string): boolean {
    return viewedRecordings.has(replayId);
  }
  function playReplayAtIndex(replayIdx: number, selectedTime?: number) {
    setCurrentRecordingIdx(replayIdx);
    setCurrentRecordingEventSelectedTime(selectedTime !== null && selectedTime !== void 0 ? selectedTime : null);

    // reset the loaded props
    setReplayLoadedProps(null);
  }
  function updateSearchFilter(newValue: string) {
    setSearchFilter(newValue);
    if (!newValue) {
      setExpandedRowsReplayIds(new Set(currentRecording !== null && currentRecording !== void 0 && currentRecording.replayId ? [currentRecording.replayId] : []));
    } else {
      const allReplayIds = sortedReplays.map(replay => replay.replayId);
      setExpandedRowsReplayIds(new Set(allReplayIds));
    }
  }
  function updateSortOption(newValue: SortOption) {
    setSelectedSortOption(newValue);
    if (sortedReplays.length > 0) {
      const newSortedReplays = sortReplaysBySortOption(sortedReplays, newValue);
      const newIndexOfCurrentReplay = newSortedReplays.findIndex(replay => {
        var _sortedReplays$curren;
        return replay.replayId === ((_sortedReplays$curren = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren === void 0 ? void 0 : _sortedReplays$curren.replayId);
      });
      setSortedReplays(newSortedReplays);
      playReplayAtIndex(newIndexOfCurrentReplay);
    }
    mixpanel.track(RecordingPlayerTrackingEvent.Sort, _objectSpread(_objectSpread({}, getTrackingProps()), {}, {
      selected_sort_option: newValue
    }));
  }
  function isSortOptionAscending(option: SortOption) {
    return option === SortOption.LeastRecent || option === SortOption.ShortestDuration || option === SortOption.LeastEvents;
  }
  function sortReplaysBySortOption(currentReplays: RecordingMetadata[]) {
    let currentSortOption = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : selectedSortOption;
    const newSortedReplays = [...currentReplays];
    switch (currentSortOption) {
      case SortOption.MostRecent:
        return newSortedReplays.sort((a, b) => b.time - a.time);
      case SortOption.LeastRecent:
        return newSortedReplays.sort((a, b) => a.time - b.time);
      case SortOption.LongestDuration:
        return newSortedReplays.sort((a, b) => b.duration - a.duration);
      case SortOption.ShortestDuration:
        return newSortedReplays.sort((a, b) => a.duration - b.duration);
      case SortOption.MostEvents:
        return newSortedReplays.sort((a, b) => getReplayEventsForId(b.replayId).length - getReplayEventsForId(a.replayId).length);
      case SortOption.LeastEvents:
        return newSortedReplays.sort((a, b) => getReplayEventsForId(a.replayId).length - getReplayEventsForId(b.replayId).length);
    }
  }
  function persistPlayedReplay(replayId: string): void {
    const parsedValue = getPlayedReplays();
    try {
      var _userCookieUtils$getU2;
      const updatedValue = [...parsedValue, replayId];
      userLocalStorageHelper.setItem({
        key: PERSISTENCE_KEY,
        userId: (_userCookieUtils$getU2 = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU2 === void 0 ? void 0 : _userCookieUtils$getU2.id,
        value: {
          seenReplayIds: updatedValue
        }
      });
      setViewedRecordings(new Set(updatedValue));
    } catch (err) {
      reporter.warn(err, "Unable to persist ".concat(PERSISTENCE_KEY, " to localStorage."));
    }
  }
  function getPlayedReplays(): Set<string> {
    try {
      var _userCookieUtils$getU3;
      const persistedState = userLocalStorageHelper.getItem({
        key: PERSISTENCE_KEY,
        userId: (_userCookieUtils$getU3 = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU3 === void 0 ? void 0 : _userCookieUtils$getU3.id
      });
      return new Set(persistedState === null || persistedState === void 0 ? void 0 : persistedState.seenReplayIds) || new Set();
    } catch (err) {
      reporter.warn(err, "Unable to retrieve ".concat(PERSISTENCE_KEY, " value from localStorage."));
      return new Set();
    }
  }
  function updateURL(distinctId: string, replayId: string | null) {
    let timestamp: number | null = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    if (props.enableUpdateURL) {
      window.history.replaceState(null, null, getProfilePath(distinctId, null, replayId, timestamp));
    }
  }
  function findReplayIdx(replayId: string) {
    return sortedReplays.findIndex(replay => replay.replayId === replayId);
  }
  function playNextRecording(replayId: string) {
    const replayIdx = findReplayIdx(replayId);
    const nextRecordingIdx = replayIdx + 1;
    if (!searchFilter && nextRecordingIdx < sortedReplays.length && nextRecordingIdx >= 0) {
      updateExpandedRows(sortedReplays[nextRecordingIdx].replayId);
      playReplayAtIndex(nextRecordingIdx);
      setTimeout(() => scrollCurrentRecordingToTop(), 200);
    }
  }
  function updateExpandedRows(newExpandedReplayId: string) {
    var _sortedReplays$curren2;
    let collapseCurrent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    const newExpandedRowsReplayIds = new Set(expandedRowsReplayIds);
    if (collapseCurrent && newExpandedRowsReplayIds.has((_sortedReplays$curren2 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren2 === void 0 ? void 0 : _sortedReplays$curren2.replayId)) {
      var _sortedReplays$curren3;
      newExpandedRowsReplayIds.delete((_sortedReplays$curren3 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren3 === void 0 ? void 0 : _sortedReplays$curren3.replayId);
    }
    newExpandedRowsReplayIds.add(newExpandedReplayId);
    setExpandedRowsReplayIds(newExpandedRowsReplayIds);
  }
  function copyReplayLink(distinctId: string, replayId: string) {
    var _sortedReplays$curren4;
    let includeTime = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    const secondsIn = copyRecordingProgressTime - ((_sortedReplays$curren4 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren4 === void 0 ? void 0 : _sortedReplays$curren4.time);
    return getLocation().origin + getProfilePath(distinctId, null, replayId, includeTime ? Math.max(secondsIn, 0) : undefined);
  }
  function jumpToEventTime(time: number, replayId: string) {
    var _sortedReplays$curren5;
    if (replayId === ((_sortedReplays$curren5 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren5 === void 0 ? void 0 : _sortedReplays$curren5.replayId)) {
      setCurrentRecordingEventSelectedTime(time);
    } else {
      const replayIdx = findReplayIdx(replayId);
      playReplayAtIndex(replayIdx, time);
    }
    // Clear the selected time after 1 second to ensure repeated jumping to the same event is possible
    setTimeout(() => setCurrentRecordingEventSelectedTime(null), 1000);
  }
  function onToggleExpand(replayId: string) {
    const newExpandedRowsReplayIds = new Set(expandedRowsReplayIds);
    if (newExpandedRowsReplayIds.has(replayId)) {
      newExpandedRowsReplayIds.delete(replayId);
    } else {
      newExpandedRowsReplayIds.add(replayId);
    }
    setExpandedRowsReplayIds(newExpandedRowsReplayIds);
  }
  function onFullscreenChange() {
    if (document.fullscreenElement) {
      setIsFullscreen(true);
    } else {
      setIsFullscreen(false);
    }
  }
  function onToggleFullscreen() {
    if (playlistModalRef.current) {
      if (!document.fullscreenElement && !isFullscreen) {
        playlistModalRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  }
  function scrollCurrentRecordingToTop() {
    if (playlistTableRef.current && currentSelectedRowRef.current) {
      const scrollDistance = currentSelectedRowRef.current.getBoundingClientRect().top - playlistTableRef.current.getBoundingClientRect().top;
      playlistTableRef.current.scrollBy({
        top: scrollDistance,
        behavior: "smooth"
      });
    }
  }
  function showFeedbackModal(sentiment: Sentiment) {
    promptFeedbackModal({
      feature: FeedbackForFeature.SessionReplay,
      'negative-label': "",
      placeholder: t("session-replay.feedback-placeholder"),
      'sentiment-heading': t("session-replay.feedback-description"),
      'positive-label': "",
      title: t("session-replay.feedback")
    }, {
      extraFeedbackEventName: RecordingPlayerTrackingEvent.Feedback,
      extraFeedbackEventProperties: getTrackingProps(),
      initialSentiment: sentiment
    });
  }
  function getSortFromOption(option: SortOption) {
    switch (option) {
      case SortOption.MostRecent:
        return {
          field: "time",
          order: -1
        };
      case SortOption.LeastRecent:
        return {
          field: "time",
          order: 1
        };
      case SortOption.LongestDuration:
        return {
          field: "duration",
          order: -1
        };
      case SortOption.ShortestDuration:
        return {
          field: "duration",
          order: 1
        };
      case SortOption.MostEvents:
        return {
          field: "eventCount",
          order: -1
        };
      case SortOption.LeastEvents:
        return {
          field: "eventCount",
          order: 1
        };
      default:
        return {
          field: "time",
          order: -1
        };
    }
  }
  function getClosedReplayAreaState(displayName: string) {
    var _currentProject$get$v, _currentProject$get$v2;
    return playlistCollapsed && <div className={styles.closedPlaylistInfo}>
          <ButtonReact theme={ButtonTheme.Tertiary} icon='push-right' onClick={() => setPlaylistCollapsed(false)} tooltip={{
        text: t("session-replay.expand-playlist")
      }} />
          {currentRecording && <>
              <a href={getProfilePath(currentRecording.distinctId, null)} target='_blank'>
                <TrackedUserIconReact distinctId={currentRecording.distinctId} imgSrc={currentRecording.avatarUrl} email={currentRecording.email} size={IconSizeType.Small} />
              </a>
              <div className={styles.closedPlaylistInfoText}>
                <div className={styles.closedPlaylistInfoTextTitle}>
                  {displayName !== DEFAULT_USER_NAME ? displayName : t("session-replay.user-distinct-id", {
              id: currentRecording.distinctId
            })}
                </div>
                {currentRecording.time && <div className={styles.closedPlaylistInfoTextSubtitle}>
                    {"".concat(print(getDisplayDate(currentRecording.time, (_currentProject$get$v = currentProject.get().value) === null || _currentProject$get$v === void 0 ? void 0 : _currentProject$get$v.timezone)), ", ").concat(print(getDisplayTime(currentRecording.time, (_currentProject$get$v2 = currentProject.get().value) === null || _currentProject$get$v2 === void 0 ? void 0 : _currentProject$get$v2.timezone)))}
                  </div>}
              </div>
            </>}
        </div>;
  }
  function getErrorState(): ReplayErrorStateType | EmbedErrorStateType | null {
    let filteredReplays = sortedReplays;
    if (!isStitchingEnabled) {
      filteredReplays = filteredReplays.filter(replay => !replay.isStitchedFromEvent);
    }
    if (!props.loadingReplays && !filteredReplays.length) {
      if (props.source === RecordingPlayerSource.UsersProfileLink) {
        return EmbedErrorStateType.NoReplayFound;
      }
      return ReplayErrorStateType.NoReplaysAvailable;
    }
    return null;
  }
  function getReplayAreaState() {
    return <>
        {currentRecording && getClosedReplayAreaState(getDisplayName(currentRecording.email, currentRecording.name, currentRecording.distinctId))}
        <div className={styles.feedbackContainer}>
          <ButtonReact theme={ButtonTheme.Tertiary} icon='thumbs-up' onClick={() => showFeedbackModal(Sentiment.Positive)} tooltip={{
          text: t("session-replay.feedback-tooltip")
        }} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx" />
          <ButtonReact theme={ButtonTheme.Tertiary} icon='thumbs-down' onClick={() => showFeedbackModal(Sentiment.Negative)} tooltip={{
          text: t("session-replay.feedback-tooltip")
        }} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx" />
        </div>
      </>;
  }
  function getPlaylist() {
    const sortInfo = getSortFromOption(selectedSortOption);
    let filteredReplays = sortedReplays;
    if (!isStitchingEnabled) {
      filteredReplays = filteredReplays.filter(replay => !replay.isStitchedFromEvent);
    }
    return <div className={"".concat(styles.replaysListItemContainer)} role={"rowgroup"} ref={playlistTableRef} data-sentry-component="getPlaylist" data-sentry-source-file="index.tsx">
        <TableReact emptyMessage={t("session-replay.no-replays-found")} loading={props.loadingReplays} defaultSortField={sortInfo.field} defaultSortOrder={sortInfo.order} searchFilter={searchFilter} className={styles.replaysListTable} rows={filteredReplays.map(replay => {
        var _sortedReplays$curren6;
        return _objectSpread(_objectSpread({}, replay), {}, {
          isExpanded: expandedRowsReplayIds.has(replay.replayId),
          eventCount: getReplayEventsForId(replay.replayId).length,
          replayEvents: getReplayEventsForId(replay.replayId),
          isSticky: replay.replayId === ((_sortedReplays$curren6 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren6 === void 0 ? void 0 : _sortedReplays$curren6.replayId)
        });
      })} columns={[{
        field: "name",
        flexGrow: "1",
        cellRenderer: (replay: RecordingMetadata) => {
          var _sortedReplays$curren7, _currentProject$get$v3, _currentProject$get$v4, _sortedReplays$curren9;
          return ReplayPlaylistRowEl({
            sel: ".".concat(styles.replayPlaylistRowEl),
            sortedReplays,
            currentRecordingIdx,
            currentlyPlaying: replay.replayId === ((_sortedReplays$curren7 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren7 === void 0 ? void 0 : _sortedReplays$curren7.replayId) && !currentRecordingPaused,
            hasBeenPlayed: hasBeenPlayed(replay.replayId),
            replay,
            replayEvents: getReplayEventsForId(replay.replayId) || [],
            replayPageViewEventsLoaded,
            replayPageViewEvents: getReplayPageViewEventsForId(replay.replayId) || [],
            displayName: getDisplayName(replay.email, replay.name, replay.distinctId),
            displayDate: print(getDisplayDate(replay.time, (_currentProject$get$v3 = currentProject.get().value) === null || _currentProject$get$v3 === void 0 ? void 0 : _currentProject$get$v3.timezone)),
            displayTime: print(getDisplayTime(replay.time, (_currentProject$get$v4 = currentProject.get().value) === null || _currentProject$get$v4 === void 0 ? void 0 : _currentProject$get$v4.timezone)),
            searchFilter,
            isExpanded: expandedRowsReplayIds.has(replay.replayId),
            onToggleExpand,
            onTogglePlay: replayId => {
              var _sortedReplays$curren8;
              if (replayId === ((_sortedReplays$curren8 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren8 === void 0 ? void 0 : _sortedReplays$curren8.replayId)) {
                setCurrentRecordingPaused(!currentRecordingPaused);
              }
            },
            onHover: (hovered, currentTarget) => {
              if (hovered) {
                const currentIdx = findReplayIdx(replay.replayId);
                setCurrentContextMenuRecordingIdx(currentIdx);
                currentContextMenuRowRef.current = currentTarget;
              } else {
                setCurrentContextMenuRecordingIdx(null);
              }
            },
            ref: replay.replayId === ((_sortedReplays$curren9 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren9 === void 0 ? void 0 : _sortedReplays$curren9.replayId) ? currentSelectedRowRef : null,
            isStitchingEnabled
          });
        },
        labelTransform: (row, _field) => {
          var _currentProject$get$v5, _currentProject$get$v6;
          const eventNames = new Set(row.replayEvents.map((event: RecordingEventMetadata) => event.eventName));
          const eventNamesString = Array.from(eventNames).join(" ");
          const propertyNamesString = row.replayEvents.map((event: RecordingEventMetadata) => {
            return Array.from(event.propertyMapping.entries()).map(_ref7 => {
              let [key, value] = _ref7;
              return "".concat(key, ": ").concat(value);
            }).join(" ");
          }).join(" ");
          return "".concat(getDisplayName(row.email, row.name, row.distinctId), " ").concat(print(getDisplayDate(row.time, (_currentProject$get$v5 = currentProject.get().value) === null || _currentProject$get$v5 === void 0 ? void 0 : _currentProject$get$v5.timezone)), " ").concat(print(getDisplayTime(row.time, (_currentProject$get$v6 = currentProject.get().value) === null || _currentProject$get$v6 === void 0 ? void 0 : _currentProject$get$v6.timezone)), " ").concat(row.distinctId, " ").concat(eventNamesString, " ").concat(propertyNamesString);
        },
        isSearchable: true
      }]} rowDetailsRenderer={(replay: RecordingMetadata) => {
        var _sortedReplays$curren10;
        return ReplayPlaylistRowEventMetadataEl({
          sel: ".".concat(styles.replayPlaylistRowEventMetadataEl),
          loadingEventMetadata: recordingsEventMetadataLoading,
          replay,
          replayEvents: getReplayEventsForId(replay.replayId),
          jumpToEvent: jumpToEventTime,
          currentRecordingProgressTime: replay.replayId === ((_sortedReplays$curren10 = sortedReplays[currentRecordingIdx]) === null || _sortedReplays$curren10 === void 0 ? void 0 : _sortedReplays$curren10.replayId) ? currentRecordingProgressTime : null,
          searchFilter,
          isStitchingEnabled,
          currentHoveredEvent: hoveredEvent,
          setCurrentHoveredEvent: setHoveredEvent,
          selectedPropertyCount: filterPinnedReplayProperties(selectedProperties).length
        });
      }} windowScroll={false} hideTitles onRowClick={_ref8 => {
        let {
          detail
        } = _ref8;
        const replayIdx = findReplayIdx(detail.row.replayId as string);
        updateExpandedRows(detail.row.replayId);
        playReplayAtIndex(replayIdx);
        setTimeout(() => scrollCurrentRecordingToTop(), 200);
      }} onNumResultsChange={_ref9 => {
        let {
          detail
        } = _ref9;
        setVisibleRecordingsCount(detail.numResults);
      }} disableExpandableRows disableExpandableRowsIndent disableRowBackgroundHover stickyRowMode style-override={tableCss + eventIndicatorCss} data-sentry-element="TableReact" data-sentry-source-file="index.tsx" />
      </div>;
  }
  let formattedRecordingProgress = "00:00";
  if (sortedReplays.length > 0) {
    const formatRecordingProgressTimeBarResult = formatRecordingProgressTimeBar((copyRecordingProgressTime - currentRecording.time) * 1000, currentRecording.duration);
    formattedRecordingProgress = formatRecordingProgressTimeBarResult.formattedProgress;
  }
  let currentContextMenuRecording = null;
  if (currentContextMenuRecordingIdx !== null && sortedReplays.length > currentContextMenuRecordingIdx) {
    currentContextMenuRecording = sortedReplays[currentContextMenuRecordingIdx];
  }
  return <div data-sentry-component="RecordingPlaylistPlayer" data-sentry-source-file="index.tsx">
      <ModalReact modalType={ModalType.Modal} open={props.open} growToContent closeable elref='recordingPlayerModal' onChange={_ref10 => {
      let {
        detail
      } = _ref10;
      if (detail.state === Visibility.Closed) {
        if (sortedReplays.length > 0) {
          updateURL(currentRecording.distinctId, null);
        }
        mixpanel.track(RecordingPlayerTrackingEvent.Close, getTrackingProps());
        props.onClose();
      } else if (detail.state === Visibility.Open) {
        if (sortedReplays.length > 0) {
          updateURL(currentRecording.distinctId, currentRecording.replayId);
        }
        mixpanel.track(RecordingPlayerTrackingEvent.Open, getTrackingProps());
      }
    }} data-sentry-element="ModalReact" data-sentry-source-file="index.tsx">
        <div className={styles.recordingModal} ref={playlistModalRef}>
          {!playlistCollapsed && <div className={"".concat(styles.replaysList)}>
              <div className={"".concat(styles.replaysListHeader)}>
                <div className={styles.replaysListHeaderText}>
                  <div className={styles.replaysListHeaderTitle}>{t("session-replay.replays")}</div>
                  {!props.loadingReplays && <div className={styles.replaysListHeaderSubtitle}>
                      {t("session-replay.replays-count", {
                  number: visibleRecordingsCount
                })}
                    </div>}
                </div>
                <ButtonReact theme={ButtonTheme.Tertiary} icon='push-left' onClick={() => setPlaylistCollapsed(true)} notFocusable tooltip={{
              text: t("session-replay.collapse-playlist")
            }} />
              </div>
              <div className={styles.replaysListControlsTop}>
                <InputReact icon={"search"} placeholder={t("session-replay.search-placeholder")} size={InputSize.Medium} value={searchFilter} onInput={ev => updateSearchFilter(ev.target.value)} showClear onClear={() => updateSearchFilter("")} />
                <SelectReact theme={SelectTheme.Secondary} iconOnly icon={{
              leftIcon: isSortOptionAscending(selectedSortOption) ? "sort-value-asc" : "sort-value-desc"
            }} menuWidth={150} sections={[{
              items: Object.keys(SortOption).map(option => ({
                value: option,
                label: print(SortOptionLabels[option])
              }))
            }]} options={Object.values(SortOption)} onChange={ev => {
              updateSortOption(ev.detail.item.value as SortOption);
            }} selectedOption={selectedSortOption} tooltip={{
              text: t("session-replay.sort-tooltip")
            }} />
                <ButtonReact theme={ButtonTheme.Secondary} icon='column-select' onClick={() => setCustomizePropertiesMenuOpen(!customizePropertiesMenuOpen)} tooltip={{
              text: t("session-replay.customize-properties")
            }} ref={customizePropertiesButtonRef} />
                <DropMenuReact open={customizePropertiesMenuOpen} onChange={ev => {
              if (ev.detail.state === Visibility.Closed) {
                setCustomizePropertiesMenuOpen(false);
              }
            }} positionParent={customizePropertiesButtonRef.current} direction={Directions.DownRight}>
                  <PropertiesMenuReact open={customizePropertiesMenuOpen} selected={selectedProperties} multiple hideRecentlyViewed hideEvents={false} hideExperiments excludeDimensionProperties customWidth={340} maxSelection={6} excludeClearProperties={getPinnedReplayPropertyNames()} disabledItems={getPinnedReplayPropertyDisabledStatus()} minSelection={PINNED_REPLAY_PROPERTIES.length} onSelect={ev => {
                setSelectedProperties(ev.item as Property[]);
                setCustomizePropertiesMenuOpen(false);
              }} />
                </DropMenuReact>
              </div>
              <div className={clsx({
            [styles.replayBannerContainerDismissible]: replayBannerType === ReplayBannerType.Dismissible,
            [styles.replayBannerContainerNonDismissible]: replayBannerType === ReplayBannerType.NonDismissible
          })}>
                <div className={clsx({
              [styles.replayBannerNonDismissiblePosition]: replayBannerType === ReplayBannerType.NonDismissible
            })}>
                  <ReplayBanner bannerType={replayBannerType} />
                </div>
                {replayBannerType === ReplayBannerType.NonDismissible && <div className={styles.blurredRow}>
                    <DummyPlaceHolderReplayPlaylistRow />
                  </div>}
              </div>
              {getPlaylist()}
              <div className={styles.replaysListControlsBottom}>
                <div className={styles.enableStitching}>
                  <span>{t("session-replay.enable-stitching")}</span>
                  <div>
                    <SvgIconReact className={styles.infoIcon} icon='info' />
                    <TooltipReact maxWidth={"256px"} text={t("session-replay.enable-stitching-tooltip", {
                  docsLink: "https://docs.mixpanel.com/docs/session-replay/implement-session-replay/session-replay-web#server-side-stitching"
                })} />
                  </div>
                </div>
                <div>
                  <SwitchReact on={isStitchingEnabled} onChange={ev => {
                const isEnabled = ev.detail.on;
                setIsStitchingEnabled(isEnabled);
                mixpanel.track(RecordingPlayerTrackingEvent.ToggleStitching, _objectSpread(_objectSpread({}, getTrackingProps()), {}, {
                  is_stitching_enabled: isEnabled
                }));
                if (!isEnabled && currentRecording !== null && currentRecording !== void 0 && currentRecording.isStitchedFromEvent) {
                  const nextObservedRecording = sortedReplays.find(replay => !replay.isStitchedFromEvent);
                  if (nextObservedRecording) {
                    playNextRecording(nextObservedRecording.replayId);
                  } else {
                    setCurrentRecordingPaused(true);
                  }
                }
              }} />
                </div>
              </div>
            </div>}
          <RecordingPlayer replay={currentRecording} replayEvents={getReplayEventsForId(currentRecording === null || currentRecording === void 0 ? void 0 : currentRecording.replayId)} isLoading={props.loadingReplays} onStartReplay={replayId => {
          const replayIdx = findReplayIdx(replayId);
          updateExpandedRows(replayId, false);
          updateURL(sortedReplays[replayIdx].distinctId, replayId);
          mixpanel.track(RecordingPlayerTrackingEvent.Watch, getTrackingProps(sortedReplays[replayIdx]));
        }} onFinishReplay={replayId => {
          persistPlayedReplay(replayId);
          playNextRecording(replayId);
          mixpanel.track(RecordingPlayerTrackingEvent.Finish, getTrackingProps(sortedReplays[findReplayIdx(replayId)]));
        }} onHalfwayThrough={replayId => {
          if (!viewedRecordings.has(replayId)) {
            persistPlayedReplay(replayId);
          }
        }} onToggleFullscreen={() => onToggleFullscreen()} onToggleSkipInactivity={on => {
          setIsSkipInactivityEnabled(on);
          mixpanel.track(RecordingPlayerTrackingEvent.ToggleSkipInactivity, _objectSpread(_objectSpread({}, getTrackingProps()), {}, {
            is_skip_inactivity_enabled: on
          }));
        }} onToggleCurrentRecordingPaused={paused => {
          setCurrentRecordingPaused(paused);
        }} onTrackReplayLoaded={loadedProps => {
          mixpanel.track(RecordingPlayerTrackingEvent.ReplayLoaded, _objectSpread(_objectSpread({}, getTrackingProps()), loadedProps));
          setReplayLoadedProps(loadedProps);
        }} isSkipInactivityEnabled={isSkipInactivityEnabled} showSkipInactiveSwitch={enableSkipInactivity} replayPageViewEventsLoaded={replayPageViewEventsLoaded} replayPageViewEvents={getReplayPageViewEventsForId(currentRecording === null || currentRecording === void 0 ? void 0 : currentRecording.replayId) || []} hasReplayPageViewEvents={hasReplayPageViewEvents() || hasReplayPageViewEventsFromEventsQuery()} updateCurrentReplayEvent={time => {
          setCurrentRecordingProgressTime(time);
        }} currentRecordingEventSelectedTime={currentRecordingEventSelectedTime} currentRecordingPaused={currentRecordingPaused} playlistCollapsed={playlistCollapsed} isFullscreen={isFullscreen} searchFilter={searchFilter} copyButton={sortedReplays.length !== 0 && <CopyToClipboardButtonReact className={styles.copyButton} copyType={CopyType.Link} onCopy={ev => {
          mixpanel.track(RecordingPlayerTrackingEvent.CopyLink, _objectSpread(_objectSpread({}, getTrackingProps()), {}, {
            copied_url: ev.detail.value
          }));
        }} options={[{
          value: copyReplayLink(currentRecording.distinctId, currentRecording.replayId),
          label: t("session-replay.copy-link")
        }, {
          value: copyReplayLink(currentRecording.distinctId, currentRecording.replayId, true),
          label: t("session-replay.copy-link-position", {
            position: formattedRecordingProgress
          })
        }]} theme={ButtonTheme.Tertiary} onClick={() => {
          setCopyRecordingProgressTime(currentRecordingProgressTime);
        }} />} isStitchingEnabled={isStitchingEnabled} errorState={getErrorState()} currentHoveredEvent={hoveredEvent} setCurrentHoveredEvent={setHoveredEvent} data-sentry-element="RecordingPlayer" data-sentry-source-file="index.tsx">
            {getReplayAreaState()}
          </RecordingPlayer>
        </div>
        {currentContextMenuRecording !== null && !customizePropertiesMenuOpen && <ReplayPlaylistRowContextMenuReact contextPanelOpen={currentContextMenuRecording !== null} replay={currentContextMenuRecording} replayPageViewEvents={getReplayPageViewEventsForId(currentContextMenuRecording.replayId) || []} replayPageViewEventsLoaded={replayPageViewEventsLoaded} displayName={getDisplayName(currentContextMenuRecording.email, currentContextMenuRecording.name, currentContextMenuRecording.distinctId)} sortedReplays={sortedReplays} currentRecordingIdx={currentRecordingIdx} positionParent={currentContextMenuRowRef === null || currentContextMenuRowRef === void 0 ? void 0 : currentContextMenuRowRef.current} onContextMenuHover={currentIdx => {
        setCurrentContextMenuRecordingIdx(currentIdx);
      }} />}
      </ModalReact>
    </div>;
}
export const RecordingPlaylistPlayerStyled = withStyles(RecordingPlaylistPlayer, css);
export type RecordingPlayerModalTypes = {
  replays: RecordingMetadata[];
  replayDateRange?: DateRange;
  source: RecordingPlayerSource;
  loadingReplays?: boolean;
  emptyReplays?: boolean;
  enableUpdateURL?: boolean;
  startingTimestamp?: string;
  startingReplayId?: string;
  initialEvents?: RecordingEvents[];
  disableInitialEventsSearch?: boolean;
  extraTrackingProps?: Record<string, any>;
};
type RecordingPlayerModalProps = RecordingPlayerModalTypes & InstanceProps<unknown> & Pick<IronContextRegistry, `localStorageManager`>;
const RecordingPlayerModalWrapper: React.FC<RecordingPlayerModalProps> = (props: RecordingPlayerModalProps) => {
  return <RecordingPlaylistPlayerStyled replays={props.replays} replayDateRange={props.replayDateRange} open={props.isOpen} onClose={props.onReject} source={props.source} loadingReplays={props.loadingReplays || false} enableUpdateURL={props.enableUpdateURL || false} startingTimestamp={props.startingTimestamp || null} startingReplayId={props.startingReplayId || null} initialEvents={props.initialEvents || []} localStorageManager={props.localStorageManager} data-sentry-element="RecordingPlaylistPlayerStyled" data-sentry-component="RecordingPlayerModalWrapper" data-sentry-source-file="index.tsx" />;
};

// Wrapper where you can pass in a promise to resolve the replays
type RecordingPlayerModalAsyncProps = Omit<RecordingPlayerModalProps, `replays` | `loadingReplays` | `initialEvents`> & {
  eventsAndReplaysPromise: Promise<{
    replays: RecordingMetadata[];
    events: RecordingEvents[];
  }>;
};
const RecordingPlayerModalAsyncWrapper: React.FC<RecordingPlayerModalAsyncProps> = (props: RecordingPlayerModalAsyncProps) => {
  const [replays, setReplays] = useState<RecordingMetadata[]>([]);
  const [loadingReplays, setLoadingReplays] = useState<boolean>(true);
  const [initialEvents, setInitialEvents] = useState<RecordingEvents[]>([]);
  useEffect(() => {
    const startedAt = new Date().getTime();
    props.eventsAndReplaysPromise.then(loadedEventsAndReplays => {
      setReplays(loadedEventsAndReplays.replays);
      setInitialEvents(loadedEventsAndReplays.events);
      setLoadingReplays(false);
      mixpanel.track(RecordingPlayerTrackingEvent.ListLoaded, _objectSpread(_objectSpread({}, props.extraTrackingProps), {}, {
        source: props.source,
        is_server_side_stitching_enabled: true,
        num_replays_loaded: loadedEventsAndReplays.replays.length,
        'Load time (ms)': new Date().getTime() - startedAt
      }));
    }).catch(err => {
      reporter.error(err);
      setLoadingReplays(false);
      mixpanel.track(RecordingPlayerTrackingEvent.ListLoadFailed, _objectSpread(_objectSpread({}, props.extraTrackingProps), {}, {
        source: props.source,
        is_server_side_stitching_enabled: true,
        error: err,
        'Load time (ms)': new Date().getTime() - startedAt
      }));
    });
  }, []);
  return <RecordingPlaylistPlayerStyled replays={replays} replayDateRange={props.replayDateRange} open={props.isOpen} onClose={props.onReject} source={props.source} loadingReplays={loadingReplays} enableUpdateURL={props.enableUpdateURL || false} startingTimestamp={props.startingTimestamp || null} startingReplayId={props.startingReplayId || null} initialEvents={initialEvents} disableInitialEventsSearch={props.disableInitialEventsSearch || false} localStorageManager={props.localStorageManager} extraTrackingProps={props.extraTrackingProps} data-sentry-element="RecordingPlaylistPlayerStyled" data-sentry-component="RecordingPlayerModalAsyncWrapper" data-sentry-source-file="index.tsx" />;
};
export const RecordingPlayerModal = create(RecordingPlayerModalWrapper);
export const RecordingPlayerModalAsync = create(RecordingPlayerModalAsyncWrapper);